import { createResource, createSignal, For, Show, onMount } from "solid-js";
import { api, type ErrorLogEntry, type ErrorLogSource } from "../shared";
import { Pagination } from "./Pagination";

function ErrorDetail(props: { entry: ErrorLogEntry; copied: string | null; onCopy: (text: string, id: string) => void }) {
  const { entry } = props;
  return (
    <div class="space-y-3 text-sm text-gray-300">
      <div><span class="font-medium text-gray-100">Error ID:</span> {entry.id}</div>
      <div><span class="font-medium text-gray-100">Method:</span> {entry.method ?? "-"}</div>
      <div><span class="font-medium text-gray-100">Path:</span> {entry.path ?? "-"}</div>
      <div>
        <div class="flex items-center gap-2 mb-1">
          <span class="font-medium text-gray-100">Message:</span>
          <button onClick={() => props.onCopy(entry.errorMessage, "msg")} class="text-gray-500 hover:text-gray-300 text-xs cursor-pointer shrink-0" title="Copy message">
            {props.copied === "msg" ? "✓" : "📋"}
          </button>
        </div>
        <span>{entry.errorMessage}</span>
      </div>
      <Show when={entry.queryJson}>
        <div>
          <div class="mb-1 font-medium text-gray-100">Query</div>
          <pre class="overflow-auto rounded bg-gray-950 p-3 text-xs text-gray-300">{entry.queryJson}</pre>
        </div>
      </Show>
      <Show when={entry.requestBodyJson}>
        <div>
          <div class="mb-1 font-medium text-gray-100">Request Body</div>
          <pre class="overflow-auto rounded bg-gray-950 p-3 text-xs text-gray-300">{entry.requestBodyJson}</pre>
        </div>
      </Show>
      <Show when={entry.stackTrace}>
        <div>
          <div class="flex items-center gap-2 mb-1">
            <span class="font-medium text-gray-100">Stack Trace</span>
            <button onClick={() => props.onCopy(entry.stackTrace!, "stack")} class="text-gray-500 hover:text-gray-300 text-xs cursor-pointer shrink-0" title="Copy stack trace">
              {props.copied === "stack" ? "✓" : "📋"}
            </button>
          </div>
          <pre class="max-h-60 md:max-h-80 overflow-auto rounded bg-gray-950 p-3 text-xs text-gray-300">{entry.stackTrace}</pre>
        </div>
      </Show>
    </div>
  );
}

export function ErrorsPanel() {
  const [source, setSource] = createSignal<ErrorLogSource | "">("");
  const [path, setPath] = createSignal("");
  const [statusCode, setStatusCode] = createSignal("");
  const [page, setPage] = createSignal(1);
  const [perPage, setPerPage] = createSignal(10);
  const [selectedId, setSelectedId] = createSignal<string | null>(null);
  const [copied, setCopied] = createSignal<string | null>(null);
  const [filters, setFilters] = createSignal<{ source?: ErrorLogSource; path?: string; statusCode?: number }>({});

  const [logs, { refetch }] = createResource(
    () => ({ ...filters(), page: page(), perPage: perPage() }),
    (params) => api.errors.list(params),
  );

  const [selectedLog, { refetch: refetchSelected }] = createResource(
    selectedId,
    (id) => id ? api.errors.get(id) : Promise.resolve(null as ErrorLogEntry | null),
  );

  function applyFilters(e: Event) {
    e.preventDefault();
    setPage(1);
    setFilters({
      source: source() || undefined,
      path: path().trim() || undefined,
      statusCode: statusCode().trim() ? Number(statusCode()) : undefined,
    });
  }

  function doCopy(text: string, id: string) {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  }

  function toggleRow(id: string) {
    if (selectedId() === id) {
      setSelectedId(null);
    } else {
      setSelectedId(id);
      refetchSelected();
    }
  }

  return (
    <div class="space-y-4">
      <div>
        <h2 class="text-lg font-semibold text-gray-100">Error Logs</h2>
        <p class="text-sm text-gray-400">Review persisted API and dashboard failures without tailing server logs.</p>
      </div>

      <form onSubmit={applyFilters} class="grid gap-3 rounded-xl border border-gray-800 bg-gray-900/40 p-4 md:grid-cols-5">
        <label class="space-y-1 text-sm text-gray-300">
          <span>Source</span>
          <select value={source()} onChange={(e) => setSource(e.currentTarget.value as ErrorLogSource | "")} class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100">
            <option value="">All</option>
            <option value="api">API</option>
            <option value="system">System</option>
          </select>
        </label>
        <label class="space-y-1 text-sm text-gray-300">
          <span>Status</span>
          <input value={statusCode()} onInput={(e) => setStatusCode(e.currentTarget.value)} placeholder="500" class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100" />
        </label>
        <label class="space-y-1 text-sm text-gray-300 md:col-span-2">
          <span>Path</span>
          <input value={path()} onInput={(e) => setPath(e.currentTarget.value)} placeholder="/api/v1/projects/..." class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100" />
        </label>
        <div class="flex items-end gap-2">
          <button type="submit" class="rounded bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-500">Apply</button>
          <button type="button" onClick={() => refetch()} class="rounded border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800">Refresh</button>
        </div>
      </form>

      <div class="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <section class="overflow-hidden rounded-xl border border-gray-800 bg-gray-900/40">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-800 text-sm">
              <thead class="bg-gray-950/60 text-left text-xs uppercase tracking-wide text-gray-400">
                <tr>
                  <th class="px-4 py-3">Time</th>
                  <th class="px-4 py-3">Source</th>
                  <th class="px-4 py-3">Status</th>
                  <th class="px-4 py-3">Path</th>
                  <th class="px-4 py-3">Message</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-800">
                <Show when={!logs.loading} fallback={<tr><td colSpan="5" class="px-4 py-6 text-center text-gray-500">Loading error logs...</td></tr>}>
                  <For each={logs()?.items ?? []}>
                    {(entry) => (
                      <>
                        <tr
                          class={`cursor-pointer transition-colors hover:bg-gray-800/50 ${selectedId() === entry.id ? "bg-gray-800/70" : ""}`}
                          onClick={() => toggleRow(entry.id)}
                        >
                          <td class="px-4 py-3 text-gray-300">{new Date(entry.timestamp).toLocaleString()}</td>
                          <td class="px-4 py-3 text-gray-300">{entry.source}</td>
                          <td class="px-4 py-3 text-gray-300">{entry.statusCode ?? "-"}</td>
                          <td class="max-w-xs truncate px-4 py-3 text-gray-300" title={entry.path ?? ""}>{entry.path ?? "-"}</td>
                          <td class="max-w-md truncate px-4 py-3 text-gray-300" title={entry.errorMessage}>{entry.errorMessage}</td>
                        </tr>
                        <Show when={selectedId() === entry.id && selectedLog()}>
                          <tr class="xl:hidden">
                            <td colSpan="5" class="bg-gray-950/60 px-4 py-3 min-w-0">
                              <ErrorDetail entry={selectedLog()!} copied={copied()} onCopy={doCopy} />
                            </td>
                          </tr>
                        </Show>
                      </>
                    )}
                  </For>
                </Show>
              </tbody>
            </table>
          </div>
          <div class="px-4 pb-4">
            <Pagination
              page={logs()?.page ?? page()}
              totalPages={logs()?.totalPages ?? 1}
              perPage={logs()?.perPage ?? perPage()}
              total={logs()?.total ?? 0}
              loading={logs.loading}
              onPageChange={(nextPage) => setPage(nextPage)}
              onPerPageChange={(nextPerPage) => {
                setPerPage(nextPerPage);
                setPage(1);
              }}
            />
          </div>
        </section>

        <section class="hidden xl:block rounded-xl border border-gray-800 bg-gray-900/40 p-4 max-h-[calc(100vh-200px)] overflow-y-auto overflow-x-hidden">
          <h3 class="text-sm font-semibold text-gray-100">Error Detail</h3>
          <Show when={selectedLog()} fallback={<p class="mt-3 text-sm text-gray-500">Select an error row to inspect its stored detail.</p>}>
            {(entry) => (
              <div class="mt-3">
                <ErrorDetail entry={entry()} copied={copied()} onCopy={doCopy} />
              </div>
            )}
          </Show>
        </section>
      </div>
    </div>
  );
}
